import { useState } from 'react';

interface SlackMessage {
  who: string;
  initials: string;
  color: string;
  text: string | React.ReactNode;
  time: string;
}

const BEFORE_MESSAGES: SlackMessage[] = [
  { who: 'Priya', initials: 'PM', color: 'var(--color-lilac)',
    text: 'Hey, can someone share the latest roadmap?', time: '9:14 AM' },
  { who: 'Marco', initials: 'MR', color: 'var(--color-mint)',
    text: 'I think it was in #planning? Lemme dig…', time: '9:16 AM' },
  { who: 'Dan', initials: 'DK', color: 'var(--color-sky)',
    text: 'I have a link from Tuesday but not sure if it\'s the latest', time: '9:18 AM' },
  { who: 'Marco', initials: 'MR', color: 'var(--color-mint)',
    text: <>https://acme.notion.site/<wbr/>Q4-2026-Roadmap-a847b92c?<wbr/>v=outdated</>, time: '9:21 AM' },
  { who: 'Priya', initials: 'PM', color: 'var(--color-lilac)',
    text: 'That one\'s from August. I\'ll ping the PM.', time: '9:23 AM' },
  { who: 'You', initials: 'YO', color: 'var(--color-peach)',
    text: 'It\'s in the Q4 Planning channel I think', time: '9:35 AM' },
];

const AFTER_MESSAGES: SlackMessage[] = [
  { who: 'Priya', initials: 'PM', color: 'var(--color-lilac)',
    text: 'Hey, can someone share the latest roadmap?', time: '9:14 AM' },
  { who: 'Marco', initials: 'MR', color: 'var(--color-mint)',
    text: <>It's at <span className="font-mono font-semibold" style={{ color: 'var(--accent-deep)' }}>go/roadmap</span></>, time: '9:14 AM' },
  { who: 'Priya', initials: 'PM', color: 'var(--color-lilac)',
    text: '🙏', time: '9:15 AM' },
];

interface SlackChannelProps {
  messages: SlackMessage[];
  channelName: string;
  label: string;
}

function SlackChannel({ messages, channelName, label }: SlackChannelProps) {
  return (
    <div
      className="bg-surface flex flex-col"
      style={{
        border: '1px solid var(--color-hairline)',
        borderRadius: 20,
        boxShadow: 'var(--shadow-md)',
        overflow: 'hidden',
        minHeight: 380,
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--color-hairline)',
          background: 'var(--color-bg-alt)',
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-ink-muted">#</span>
          <span className="text-[13px] font-semibold text-ink">{channelName}</span>
        </div>
        <span
          className="font-bold uppercase text-[10px]"
          style={{ color: 'var(--color-ink-muted)', letterSpacing: '0.08em' }}
        >
          {label}
        </span>
      </div>
      <div className="flex-1 flex flex-col gap-3 p-5 overflow-y-auto">
        {messages.map((m, i) => (
          <div key={i} className="flex gap-2.5 items-start">
            <div
              className="rounded flex items-center justify-center font-bold text-[10px] shrink-0"
              style={{
                width: 30,
                height: 30,
                background: m.color,
                color: 'var(--color-ink)',
              }}
            >
              {m.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-[13px] font-semibold text-ink">{m.who}</span>
                <span className="text-[11px] text-ink-muted">{m.time}</span>
              </div>
              <div className="text-sm text-ink leading-[1.4] mt-0.5 break-words">{m.text}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [side, setSide] = useState<'before' | 'after'>('after');

  return (
    <div className="max-w-[1040px] mx-auto">
      <div
        className="inline-flex bg-bg-alt mx-auto mb-8 mb-10 p-[3px]"
        style={{
          border: '1px solid var(--color-hairline)',
          borderRadius: 14,
        }}
      >
        {(['before', 'after'] as const).map(s => {
          const active = side === s;
          return (
            <button
              key={s}
              onClick={() => setSide(s)}
              className="cursor-pointer transition-all duration-150"
              style={{
                padding: '8px 18px',
                background: active ? 'var(--color-surface)' : 'transparent',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: active ? 'var(--accent-deep)' : 'var(--color-ink-soft)',
                fontFamily: 'inherit',
                boxShadow: active ? 'var(--shadow-sm)' : 'none',
                textTransform: 'capitalize',
              }}
            >
              {s === 'before' ? '😩 Before' : '✨ After'}
            </button>
          );
        })}
      </div>

      <div className="grid gap-6" style={{ gridTemplateColumns: side === 'before' ? '1fr' : '1fr', maxWidth: 720, margin: '0 auto' }}>
        {side === 'before' ? (
          <SlackChannel messages={BEFORE_MESSAGES} channelName="general" label="22 minutes of pinging" />
        ) : (
          <SlackChannel messages={AFTER_MESSAGES} channelName="general" label="60 seconds, done" />
        )}
      </div>

      <div className="text-center mt-7 text-sm text-ink-muted">
        Same question, two different worlds.{' '}
        <span className="text-ink-soft">
          {side === 'before' ? 'You haven\'t set up go/ links yet.' : 'go/ links live in your team\'s muscle memory.'}
        </span>
      </div>
    </div>
  );
}
